import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RevealText, Reveal } from "@/components/RevealText";
import { getJobs, submitApplication } from "@/lib/api";

const initial = { name: "", email: "", phone: "", role: "", portfolio: "", message: "" };

const PERKS = [
  { n: "01", t: "Remote-first", d: "Work from where you do your best thinking. Outcomes over hours." },
  { n: "02", t: "Real ownership", d: "Ship work that matters with your name on it — no bureaucracy." },
  { n: "03", t: "Learn constantly", d: "A front-row seat to AI, modern web and software at the edge." },
  { n: "04", t: "Craft culture", d: "We sweat the details. If that excites you, you'll feel at home." },
];

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    getJobs().then(setJobs).catch(() => {});
  }, []);

  const applyTo = (title) => {
    setSent(false);
    setForm((f) => ({ ...f, role: title }));
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role || !form.message) {
      toast.error("Please fill name, email, role and a short message.");
      return;
    }
    setBusy(true);
    try {
      await submitApplication(form);
      setSent(true);
      setForm(initial);
      toast.success("Application sent. Thank you — we'll review it.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 md:pt-44" data-testid="careers-page">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24 border-b border-white/10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-8 flex items-center gap-3">
            <span className="w-8 h-px bg-[color:var(--accent)]" /> Careers
          </p>
          <h1 className="font-display text-6xl md:text-[8.5vw] uppercase tracking-tighter leading-[0.82]">
            <RevealText lines={["Build the"]} />
            <span className="block text-white/25"><RevealText lines={["work you're"]} delay={0.12} /></span>
            <RevealText lines={["proud of."]} delay={0.24} />
          </h1>
        </div>
        <div className="lg:col-span-4">
          <div className="aspect-[4/3] border border-white/10 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1551649081-716ed4a46706?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Join Liinex" className="w-full h-full object-cover grayscale" />
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-white/10">
          {PERKS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.08} className="border-r border-b border-white/10 p-8 md:p-10">
              <span className="font-mono text-xs tracking-[0.25em] text-[color:var(--accent)] mb-6 block">{p.n}</span>
              <h3 className="font-display text-2xl uppercase tracking-tight mb-3">{p.t}</h3>
              <p className="font-mono text-sm text-white/50 leading-relaxed">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Open roles */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 border-t border-white/10" data-testid="open-roles">
        <div className="flex items-end justify-between mb-14">
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.85]">Open<br />positions</h2>
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-white/40">{jobs.length} roles</span>
        </div>
        <div className="border-t border-white/10">
          {jobs.map((job, i) => (
            <Reveal key={job.id} delay={i * 0.05}>
              <div className="group border-b border-white/10 py-8 md:py-10 grid grid-cols-1 md:grid-cols-12 gap-4 md:items-center hover:bg-white/[0.02] transition-colors px-2" data-testid={`job-${job.id}`}>
                <div className="md:col-span-5">
                  <h3 className="font-display text-2xl md:text-4xl uppercase tracking-tight group-hover:translate-x-2 transition-transform">{job.title}</h3>
                </div>
                <div className="md:col-span-2 font-mono text-xs tracking-[0.15em] uppercase text-white/50">{job.team}</div>
                <div className="md:col-span-2 font-mono text-xs tracking-[0.15em] uppercase text-white/50">{job.type}</div>
                <div className="md:col-span-2 font-mono text-xs tracking-[0.15em] uppercase text-white/50">{job.location}</div>
                <div className="md:col-span-1 flex md:justify-end">
                  <button onClick={() => applyTo(job.title)} className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-white/70 hover:text-[color:var(--accent)] transition-colors" data-testid={`apply-${job.id}`}>
                    Apply <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section ref={formRef} className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-32 border-t border-white/10 scroll-mt-24" data-testid="application-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--accent)]" /> Apply
            </p>
            <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.85]">Send your<br />application</h2>
            <p className="font-mono text-sm text-white/50 mt-6 max-w-xs">Don't see a perfect fit? Apply anyway — we're always looking for exceptional people.</p>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            {sent ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/15 p-10 md:p-16" data-testid="application-success">
                <span className="w-3 h-3 bg-[color:var(--accent)] inline-block mb-8" />
                <h3 className="font-display text-4xl uppercase tracking-tight mb-4">Application received.</h3>
                <p className="font-mono text-sm text-white/55 max-w-md">Thanks for applying to Liinex. If there's a match, we'll reach out to schedule a conversation.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-10" data-testid="application-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <Field label="Full name *" value={form.name} onChange={set("name")} placeholder="Jane Doe" testid="app-name" />
                  <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" testid="app-email" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="+91 …" testid="app-phone" />
                  <div>
                    <label className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-3 block">Role *</label>
                    <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                      <SelectTrigger className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 h-auto py-3 font-mono text-white focus:ring-0 focus:border-white" data-testid="app-role">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-[color:var(--surface)] border-white/15 font-mono">
                        {jobs.map((j) => (
                          <SelectItem key={j.id} value={j.title} className="rounded-none focus:bg-white/10">{j.title}</SelectItem>
                        ))}
                        <SelectItem value="Open application" className="rounded-none focus:bg-white/10">Open application</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Field label="Portfolio / LinkedIn" value={form.portfolio} onChange={set("portfolio")} placeholder="https://…" testid="app-portfolio" />
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-3 block">Why Liinex? *</label>
                  <textarea value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about yourself…" className="w-full bg-transparent border-b border-white/20 focus:border-white focus:outline-none placeholder-white/30 font-mono py-3 resize-none text-white" data-testid="app-message" />
                </div>
                <button type="submit" disabled={busy} className="group inline-flex items-center gap-3 bg-white text-black px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors disabled:opacity-50" data-testid="application-submit">
                  {busy ? "Sending…" : "Submit application"}
                  <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-3 block">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full bg-transparent border-b border-white/20 focus:border-white focus:outline-none placeholder-white/30 font-mono py-3 text-white" data-testid={testid} />
    </div>
  );
}
